import React, { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { useAuth } from 'auth';
import Button from 'components/atoms/button';
import { Icon } from 'components/atoms';
import { Icons } from 'components/atoms/icon/types';
import {
  AllowedFiles,
  FileFormat,
  FileInformationResponse,
  getFileExtension,
} from 'helpers/documentHelper';
import { useConfig } from 'config';
import { useFetch, useSignalR } from 'hooks';
import { ThirdPartyType } from 'types';

const ControlsContainer = styled.div<{ compact?: boolean }>`
  margin-bottom: ${({ compact }) => (compact ? '0' : '16px')};
  width: 100%;
`;

const TriggerButton = styled(Button)<{ plainMenuText?: boolean }>`
  width: ${({ plainMenuText }) => (plainMenuText ? '100%' : 'auto')};

  ${({ plainMenuText, theme }) =>
    plainMenuText &&
    `
			min-height: auto;
			height: auto;
			padding: 0;
			border: 0;
			background: transparent;
			color: ${theme.colours.darkToneInk};
			text-decoration: none;
			font-size: ${theme.pxToRem(14)};
			line-height: 1.5;
			justify-content: flex-start;

			&:hover {
				background: transparent;
				border: 0;
				color: ${theme.colours.darkToneInk};
				text-decoration: none;
			}

			&:disabled {
				background: transparent;
				border: 0;
				color: ${theme.colours.spanishGrey};
				text-decoration: none;
			}
		`}
`;

const WarningMessage = styled.p`
  color: ${({ theme }) => theme.colours.redRampage};
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.pxToRem(14)};
  line-height: 1.5;
  margin-top: ${({ theme }) => theme.spacing(8)};
`;

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(8)};
`;

interface DocumentCategoryOption {
  id: number;
  category: string;
}

export interface PendingDocumentUpload {
  uploadId: string;
  name: string;
  size: number;
  type: string;
  failed?: boolean;
  errorMessage?: string;
}

interface BulkFileUploadItemResponse {
  fileName: string;
  success: boolean;
  file?: FileInformationResponse;
  errorCode?: string;
  message?: string;
}

interface BulkFileUploadResponse {
  total: number;
  succeeded: number;
  failed: number;
  files: BulkFileUploadItemResponse[];
}

interface QueuedDocumentUpload {
  execute: () => Promise<void>;
}

const MAX_CONCURRENT_UPLOADS = 5;

const isBulkFileUploadResponse = (
  responseData: BulkFileUploadResponse | FileInformationResponse | FileInformationResponse[]
): responseData is BulkFileUploadResponse =>
  !Array.isArray(responseData) && 'files' in responseData;

interface DocumentBulkFileInputProps {
  documentsCount: number;
  entityType: ThirdPartyType;
  label?: string;
  buttonText?: string;
  buttonIcon?: Icons | null;
  showLabel?: boolean;
  outlineButton?: boolean;
  secondaryButton?: boolean;
  textButton?: boolean;
  smallButton?: boolean;
  plainMenuText?: boolean;
  maxDocuments?: number;
  maxFilesPerUpload?: number;
  maxFilesPerUploadExceededMessage?: string;
  maxDocumentsReachedMessage?: string;
  hideValidationWarning?: boolean;
  categoryId?: number;
  existingFileNames?: string[];
  files?: File[];
  showValidationErrorsAsPendingCards?: boolean;
  uploadImmediately?: boolean;
  isBulkUpload?: boolean;
  thirdPartyId?: string;
  signalRConnectionId?: string;
  linkToThirdParty?: boolean;
  notifyAnalyst?: boolean;
  disabled?: boolean;
  onFilesSelected?: () => void;
  onFilesChange?: (files: File[]) => void;
  onValidationError?: (errorMessage?: string) => void;
  onSignalRMessage?: (message: unknown) => void;
  onPendingFileUploadsStart?: (pendingUploads: PendingDocumentUpload[]) => void;
  onPendingFileUploadComplete?: (uploadId: string) => void;
  onPendingFileUploadError?: (uploadId: string, errorMessage: string) => void;
  onSuccessDocumentUpload?: (
    fileInformation: FileInformationResponse,
    pendingUpload?: PendingDocumentUpload
  ) => void;
  onPendingUploadsChange?: (pendingUploadsCount: number) => void;
}

const DocumentBulkFileInput: FC<DocumentBulkFileInputProps> = ({
  documentsCount,
  entityType,
  label = 'Upload Documents',
  buttonText = 'Upload Documents',
  buttonIcon = 'download',
  showLabel = true,
  outlineButton = true,
  secondaryButton = false,
  textButton = false,
  smallButton = true,
  plainMenuText = false,
  maxDocuments = 60,
  maxFilesPerUpload = 60,
  maxFilesPerUploadExceededMessage,
  maxDocumentsReachedMessage,
  hideValidationWarning = false,
  categoryId,
  existingFileNames = [],
  files = [],
  showValidationErrorsAsPendingCards = false,
  uploadImmediately = true,
  isBulkUpload = false,
  thirdPartyId,
  signalRConnectionId,
  linkToThirdParty = true,
  notifyAnalyst = false,
  disabled = false,
  onFilesSelected,
  onFilesChange,
  onValidationError,
  onSignalRMessage,
  onPendingFileUploadsStart,
  onPendingFileUploadComplete,
  onPendingFileUploadError,
  onSuccessDocumentUpload,
  onPendingUploadsChange,
}: DocumentBulkFileInputProps) => {
  const { tokenInfo } = useAuth();
  const { vantageWebApi, msalSubscriptionKey, xFunctionsKey } = useConfig();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadQueueRef = useRef<QueuedDocumentUpload[]>([]);
  const activeUploadsCountRef = useRef(0);
  const isMountedRef = useRef(true);
  const [signalRMessage, setSignalRMessage] = useState<unknown>(null);
  const [warningMessage, setWarningMessage] = useState<string>();
  const hasExternalSignalRConnection = signalRConnectionId !== undefined;
  const { connectionId: internalConnectionId } = useSignalR(
    setSignalRMessage,
    'fileupload',
    '',
    uploadImmediately && !hasExternalSignalRConnection
  );
  const connectionId = signalRConnectionId || internalConnectionId;

  useEffect(() => {
    if (signalRMessage) {
      onSignalRMessage?.(signalRMessage);
    }
  }, [onSignalRMessage, signalRMessage]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      uploadQueueRef.current = [];
    };
  }, []);

  const { data: categoriesData, loading: categoriesLoading } = useFetch<DocumentCategoryOption[]>(
    `${vantageWebApi}/file/categories/client?entityType=${ThirdPartyType[entityType]}`,
    {
      method: 'GET',
      lazy: !uploadImmediately,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenInfo.accessToken}`,
      },
    }
  );

  const supportedFormats = useMemo(
    () =>
      Object.values(FileFormat)
        .map((format) => `.${format}`)
        .join(', '),
    []
  );

  const categoryOptions = useMemo(
    () => (Array.isArray(categoriesData) ? categoriesData : []),
    [categoriesData]
  );

  const defaultCategory = useMemo(() => {
    return categoryOptions.find(
      (categoryOption) => categoryOption.category?.toLowerCase() === 'other'
    );
  }, [categoryOptions]);

  const remainingUploads = Math.max(maxDocuments - documentsCount, 0);
  const isCategoryLoading = !categoryId && categoriesLoading;

  const getAutomaticDocumentName = (file: File) => file.name;

  const getPendingUploadId = (file: File, index: number) =>
    `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${index}`;

  const normalizeFileName = (fileName: string) => fileName.trim().toLowerCase();

  const getValidationErrorMessage = (file: File, selectedFileNames: Set<string>) => {
    const fileExtension = getFileExtension(file.name)?.toLowerCase();
    const isUnsupportedFormat = !Object.values(FileFormat).includes(fileExtension as FileFormat);
    const exceedsMaxFileSize = file.size > AllowedFiles.DocumentMaxByteSize;
    const normalizedFileName = normalizeFileName(file.name);
    const isDuplicateFile =
      selectedFileNames.has(normalizedFileName) ||
      existingFileNames.some((fileName) => normalizeFileName(fileName) === normalizedFileName);

    if (isDuplicateFile) {
      return 'Upload failed: duplicate file detected.';
    }

    if (isUnsupportedFormat && exceedsMaxFileSize) {
      return 'File format is not supported and file size exceeds 20MB.';
    }

    if (exceedsMaxFileSize) {
      return 'File size exceeds 20MB.';
    }

    if (isUnsupportedFormat) {
      return 'File format is not supported.';
    }

    return undefined;
  };

  const parseUploadErrorMessage = async (response: Response) => {
    const fallbackMessage =
      'Due to an unforeseen technical issue, it has not been possible to upload this document. Please try again.';
    const contentType = response.headers?.get('content-type');

    if (contentType?.includes('application/json')) {
      const errorData = await response.json();
      return errorData?.message ?? fallbackMessage;
    }

    const textResponse = await response.text();
    return textResponse || fallbackMessage;
  };

  const updatePendingUploadsCount = (countChange: number) => {
    onPendingUploadsChange?.(countChange);
  };

  const setValidationWarning = (message?: string) => {
    setWarningMessage(message);
    onValidationError?.(message);
  };

  const getUploadErrorMessage = (error: unknown) => {
    if (error instanceof Error) {
      return error.message;
    }

    return 'Due to an unforeseen technical issue, it has not been possible to upload this document. Please try again.';
  };

  const getPendingUploadForFileName = (fileName: string, pendingUploads: PendingDocumentUpload[]) =>
    pendingUploads.find(
      (pendingUpload) => normalizeFileName(pendingUpload.name) === normalizeFileName(fileName)
    );

  const handleBulkUploadResponse = (
    responseData: BulkFileUploadResponse,
    pendingUploads: PendingDocumentUpload[]
  ) => {
    responseData.files.forEach((item) => {
      const pendingUpload = getPendingUploadForFileName(item.fileName, pendingUploads);

      if (item.success && item.file) {
        onSuccessDocumentUpload?.(item.file, pendingUpload);
        return;
      }

      if (!item.success && pendingUpload) {
        onPendingFileUploadError?.(
          pendingUpload.uploadId,
          item.message || getUploadErrorMessage(undefined)
        );
      }
    });
  };

  const findUploadedFilePendingUploadIndex = (
    fileInformation: FileInformationResponse,
    pendingUploads: PendingDocumentUpload[]
  ) => {
    const uploadedFileNames = [fileInformation.fileDownloadName, fileInformation.name]
      .filter((fileName): fileName is string => !!fileName)
      .map(normalizeFileName);

    const hasSameName = (pendingUpload: PendingDocumentUpload) =>
      uploadedFileNames.includes(normalizeFileName(pendingUpload.name));
    const hasSameSize = (pendingUpload: PendingDocumentUpload) =>
      !fileInformation.size || pendingUpload.size === fileInformation.size;

    const exactMatchIndex = pendingUploads.findIndex(
      (pendingUpload) => hasSameName(pendingUpload) && hasSameSize(pendingUpload)
    );

    if (exactMatchIndex >= 0) {
      return exactMatchIndex;
    }

    return pendingUploads.findIndex(hasSameName);
  };

  async function uploadFile(
    file: File,
    selectedCategoryId: number,
    pendingUpload: PendingDocumentUpload
  ) {
    const formData = new FormData();
    formData.append('CategoryId', String(selectedCategoryId));
    formData.append('EntityType', entityType);
    formData.append('ConnectionId', connectionId ?? '');
    formData.append('LinkToThirdParty', String(linkToThirdParty));

    if (isBulkUpload) {
      formData.append('IsBulkUpload', 'true');
    }

    formData.append('File', file);
    formData.append('Name', getAutomaticDocumentName(file));
    formData.append('Type', file.type);
    formData.append('Size', file.size.toString());

    if (thirdPartyId) {
      formData.append('ThirdPartyId', thirdPartyId);
    }

    try {
      const response = await fetch(`${vantageWebApi}/file/upload?notifyAnalyst=${notifyAnalyst}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokenInfo.accessToken}`,
          'Ocp-Apim-Subscription-Key': msalSubscriptionKey,
          'X-functions-Key': xFunctionsKey,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(await parseUploadErrorMessage(response));
      }

      const responseData = (await response.json()) as
        | BulkFileUploadResponse
        | FileInformationResponse
        | FileInformationResponse[];

      if (isBulkFileUploadResponse(responseData)) {
        handleBulkUploadResponse(responseData, [pendingUpload]);
        return;
      }

      const uploadedFiles = Array.isArray(responseData) ? responseData : [responseData];
      const unmatchedPendingUploads = [pendingUpload];

      uploadedFiles.forEach((fileInformation) => {
        const matchingPendingUploadIndex = findUploadedFilePendingUploadIndex(
          fileInformation,
          unmatchedPendingUploads
        );
        const [matchingPendingUpload] =
          matchingPendingUploadIndex >= 0
            ? unmatchedPendingUploads.splice(matchingPendingUploadIndex, 1)
            : unmatchedPendingUploads.splice(0, 1);

        onSuccessDocumentUpload?.(fileInformation, matchingPendingUpload);
      });
    } catch (error) {
      onPendingFileUploadError?.(pendingUpload.uploadId, getUploadErrorMessage(error));
    } finally {
      updatePendingUploadsCount(-1);
      onPendingFileUploadComplete?.(pendingUpload.uploadId);
      activeUploadsCountRef.current -= 1;
      processUploadQueue();
    }
  }

  function processUploadQueue() {
    if (!isMountedRef.current) {
      return;
    }

    while (
      activeUploadsCountRef.current < MAX_CONCURRENT_UPLOADS &&
      uploadQueueRef.current.length > 0
    ) {
      const queuedUpload = uploadQueueRef.current.shift();

      if (!queuedUpload) {
        return;
      }

      activeUploadsCountRef.current += 1;
      void queuedUpload.execute();
    }
  }

  const enqueueUploads = (
    filesToUpload: File[],
    selectedCategoryId: number,
    pendingUploads: PendingDocumentUpload[]
  ) => {
    uploadQueueRef.current.push(
      ...filesToUpload.map((file, index) => ({
        execute: () => uploadFile(file, selectedCategoryId, pendingUploads[index]),
      }))
    );
    processUploadQueue();
  };

  const handleFileSelection = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);

    event.target.value = '';

    if (selectedFiles.length === 0) {
      return;
    }

    if (selectedFiles.length > maxFilesPerUpload) {
      setValidationWarning(
        maxFilesPerUploadExceededMessage ??
          `The maximum number of files allowed is ${maxFilesPerUpload}`
      );
      return;
    }

    if (remainingUploads === 0) {
      setValidationWarning(
        maxDocumentsReachedMessage ?? `You have reached the maximum of ${maxDocuments} documents.`
      );
      return;
    }

    onFilesSelected?.();

    const selectedCategoryId = categoryId ?? defaultCategory?.id;

    if (uploadImmediately && !selectedCategoryId) {
      return;
    }

    setValidationWarning(undefined);

    const selectedFileNames = new Set<string>();
    files.forEach((file) => selectedFileNames.add(normalizeFileName(file.name)));
    const filesToUpload: File[] = [];
    const validationFailedPendingUploads: PendingDocumentUpload[] = [];

    selectedFiles.slice(0, remainingUploads).forEach((file, index) => {
      const validationErrorMessage = getValidationErrorMessage(file, selectedFileNames);

      if (validationErrorMessage) {
        validationFailedPendingUploads.push({
          uploadId: getPendingUploadId(file, index),
          name: file.name,
          size: file.size,
          type: getFileExtension(file.name)?.toLowerCase(),
          failed: true,
          errorMessage: validationErrorMessage,
        });
        return;
      }

      selectedFileNames.add(normalizeFileName(file.name));
      filesToUpload.push(file);
    });

    if (!uploadImmediately) {
      if (validationFailedPendingUploads.length > 0) {
        setValidationWarning(validationFailedPendingUploads[0].errorMessage);
      } else {
        setValidationWarning(undefined);
      }

      if (filesToUpload.length > 0) {
        onFilesChange?.([...files, ...filesToUpload]);
      }

      return;
    }

    const pendingUploads = filesToUpload.map((file, index) => ({
      uploadId: getPendingUploadId(file, index),
      name: file.name,
      size: file.size,
      type: getFileExtension(file.name)?.toLowerCase(),
    }));

    updatePendingUploadsCount(filesToUpload.length);

    if (showValidationErrorsAsPendingCards) {
      onPendingFileUploadsStart?.([...pendingUploads, ...validationFailedPendingUploads]);
    } else {
      onPendingFileUploadsStart?.(pendingUploads);
      if (validationFailedPendingUploads.length > 0) {
        setWarningMessage(validationFailedPendingUploads[0].errorMessage);
      }
    }

    if (filesToUpload.length === 0) {
      return;
    }

    enqueueUploads(filesToUpload, selectedCategoryId as number, pendingUploads);
  };

  return (
    <>
      <input
        type="file"
        accept={supportedFormats}
        multiple
        ref={fileInputRef}
        onChange={handleFileSelection}
        style={{ display: 'none' }}
      />
      <ControlsContainer compact={plainMenuText}>
        {showLabel && <label>{label}</label>}
        <TriggerButton
          outline={outlineButton}
          secondary={secondaryButton}
          text={textButton}
          small={smallButton}
          plainMenuText={plainMenuText}
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || (uploadImmediately && isCategoryLoading) || remainingUploads === 0}
        >
          <ButtonContent>
            {buttonIcon !== null && buttonIcon !== undefined && (
              <Icon icon={buttonIcon} size="xxxs" aria-hidden />
            )}
            <span>{buttonText}</span>
          </ButtonContent>
        </TriggerButton>
        {!hideValidationWarning && warningMessage && (
          <WarningMessage>{warningMessage}</WarningMessage>
        )}
      </ControlsContainer>
    </>
  );
};

export default DocumentBulkFileInput;
